import {
  Card,
  Box,
  type Theme,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { type ReactNode } from 'react';

/** Semantic tones for a summary card's accent colour. */
export type SearchPageCardTone =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'info'
  | 'danger'
  | 'neutral';

/**
 * The cards tighten up by the width of the row they sit in, not the screen:
 * the same row is used full-width on a page and inside narrower panels.
 */
const COMPACT = '@container (max-width: 599.95px)';

function toneColor(theme: Theme, tone: SearchPageCardTone): string {
  if (tone === 'neutral') return theme.palette.text.secondary;
  if (tone === 'danger') return theme.palette.error.main;
  return theme.palette[tone].main;
}

/**
 * One summary card shown above a {@link SearchPage}'s results — a tinted icon
 * chip beside a big value and a caption label. Set `onClick` to turn it into a
 * clickable filter toggle (`active` highlights it).
 */
export interface SearchPageSummaryCard {
  /** Stable key; defaults to `label`. */
  key?: string;
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  tone?: SearchPageCardTone;
  /** When set, the card becomes a clickable filter. */
  onClick?: () => void;
  /** Highlight the card as the active filter. */
  active?: boolean;
}

function CardTile({ card }: { card: SearchPageSummaryCard }) {
  const theme = useTheme();
  const c = toneColor(theme, card.tone ?? 'primary');
  return (
    <Card
      variant="outlined"
      onClick={card.onClick}
      sx={{
        flex: '1 1 160px',
        minWidth: 150,
        p: 1.75,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        borderRadius: 3,
        [COMPACT]: { minWidth: 0, p: 1, gap: 1, borderRadius: 2 },
        borderColor: card.active ? c : 'divider',
        borderWidth: card.active ? 2 : 1,
        bgcolor: card.active ? alpha(c, 0.06) : 'background.paper',
        ...(card.onClick
          ? {
              cursor: 'pointer',
              transition: 'border-color 0.15s, background-color 0.15s',
              '&:hover': { borderColor: c, bgcolor: alpha(c, 0.05) },
            }
          : {}),
      }}
    >
      {card.icon && (
        <Box
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(c, 0.13),
            color: c,
            [COMPACT]: {
              width: 30,
              height: 30,
              borderRadius: 1.5,
              '& .MuiSvgIcon-root': { fontSize: 18 },
            },
          }}
        >
          {card.icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1.35rem',
            lineHeight: 1.1,
            [COMPACT]: { fontSize: '1.05rem' },
          }}
        >
          {card.value}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            fontWeight: 600,
            // One line in the tight layout: a wrapped label made a card twice
            // the height of its neighbours.
            [COMPACT]: {
              display: 'block',
              fontSize: '0.625rem',
              lineHeight: 1.3,
              letterSpacing: 0.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        >
          {card.label}
        </Typography>
      </Box>
    </Card>
  );
}

/**
 * A responsive row of {@link SearchPageSummaryCard}s — the managed rendering behind
 * {@link SearchPage}'s `cards` prop. Consumers pass a declarative array; the row
 * layout, tones, and interactive styling are owned here. The row sizes itself
 * to its own width, so it can be dropped into any panel.
 * @param props - The cards to render
 * @returns The cards row
 */
export function SearchPageCards({ cards }: { cards: SearchPageSummaryCard[] }) {
  if (!cards.length) return null;
  return (
    <Box sx={{ containerType: 'inline-size' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          // Two to a row once the row is narrow, with an odd last card taking
          // the full width rather than leaving a hole beside it.
          [COMPACT]: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 1,
            '& > :last-child:nth-of-type(odd)': { gridColumn: '1 / -1' },
          },
        }}
      >
        {cards.map((card) => (
          <CardTile key={card.key ?? card.label} card={card} />
        ))}
      </Box>
    </Box>
  );
}

/** Props for {@link SearchPageBanner}. */
export interface SearchPageBannerProps {
  title: ReactNode;
  /** A line under the title. */
  description?: ReactNode;
  icon?: ReactNode;
  tone?: SearchPageCardTone;
  /** Links or buttons at the end of the title row. */
  actions?: ReactNode;
  /** The body — instructions, download buttons, a short form. */
  children?: ReactNode;
}

/**
 * A full-width tile in the summary cards' style, for the things a page needs
 * to say above its results that aren't a number: setup instructions, a
 * download, a notice. Pass it as {@link SearchPage}'s `banner` and it folds
 * away with the header on a phone, the way the cards do.
 * @param props - Component props
 * @returns The banner tile
 */
export function SearchPageBanner({
  title,
  description = undefined,
  icon = undefined,
  tone = 'primary',
  actions = undefined,
  children = undefined,
}: SearchPageBannerProps) {
  const theme = useTheme();
  const c = toneColor(theme, tone);
  return (
    <Box sx={{ containerType: 'inline-size' }}>
      <Card
        variant="outlined"
        sx={{
          p: 1.75,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          [COMPACT]: { p: 1, gap: 1, borderRadius: 2 },
        }}
      >
        {icon && (
          <Box
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(c, 0.13),
              color: c,
              [COMPACT]: {
                width: 30,
                height: 30,
                borderRadius: 1.5,
                '& .MuiSvgIcon-root': { fontSize: 18 },
              },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              columnGap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1rem',
                lineHeight: 1.3,
                [COMPACT]: { fontSize: '0.9rem' },
              }}
            >
              {title}
            </Typography>
            {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
          </Box>
          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ [COMPACT]: { fontSize: '0.75rem' } }}
            >
              {description}
            </Typography>
          )}
          {children && (
            <Box sx={{ mt: 1.25, [COMPACT]: { mt: 0.75 } }}>{children}</Box>
          )}
        </Box>
      </Card>
    </Box>
  );
}
